import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import type { TickerBlip } from '../../components/SignalLoader/SignalLoader';
import styles from './Login.module.css';

type Mode = 'signIn' | 'signUp' | 'confirm' | 'forgotPassword' | 'resetCode' | 'mfaCode';

const Login: React.FC = () => {
  const {
    user,
    loading,
    signIn,
    confirmMfaSignIn,
    signUp,
    signOut,
    confirmSignUp,
    resendSignUpCode,
    resetPassword,
    confirmResetPassword,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/home';

  const loginTickers: TickerBlip[] = useMemo(
    () => [
      { symbol: 'NVDA', change: 2.3, signal: 'normal' },
      { symbol: 'SPY', change: 0.4, signal: 'normal' },
      { symbol: 'TSLA', change: 4.7, signal: 'hot' },
      { symbol: 'AMD', change: 1.8, signal: 'normal' },
      { symbol: 'QQQ', change: -0.6, signal: 'normal' },
      { symbol: 'AMZN', change: 1.4, signal: 'normal' },
      { symbol: 'XOM', change: -2.8, signal: 'hot' },
      { symbol: 'AAPL', change: -1.1, signal: 'normal' },
      { symbol: 'META', change: 0.7, signal: 'normal' },
      { symbol: 'COIN', change: -5.1, signal: 'hot' },
      { symbol: 'GOOGL', change: 0.9, signal: 'normal' },
      { symbol: 'PLTR', change: 6.2, signal: 'hot' },
    ],
    [],
  );

  // If already signed in, redirect to the target page
  useEffect(() => {
    if (!loading && user) {
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, from]);

  const initialMode = new URLSearchParams(location.search).get('mode') === 'signUp' ? 'signUp' : 'signIn';
  const [mode, setMode] = useState<Mode>(initialMode);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const result = await signIn(username, password);
      if (!result.isSignedIn && result.nextStep === 'TOTP') {
        setMode('mfaCode');
        setMfaCode('');
        return;
      }
      navigate(from, { replace: true });
    } catch (err: any) {
      // Unconfirmed user — redirect to confirm screen and resend code
      if (err.name === 'UserNotConfirmedException' || err.message?.includes('not confirmed')) {
        try {
          await resendSignUpCode(username);
          setSuccessMsg('We sent a new confirmation code to your email.');
        } catch {
          // Resend may fail but still show confirm screen
        }
        setError('');
        setMode('confirm');
        return;
      }
      // Stale session — clear and retry
      if (err.message?.includes('already a signed in user')) {
        try {
          await signOut();
          const result = await signIn(username, password);
          if (!result.isSignedIn && result.nextStep === 'TOTP') {
            setMode('mfaCode');
            setMfaCode('');
            return;
          }
          navigate(from, { replace: true });
          return;
        } catch (retryErr: any) {
          setError(retryErr.message || 'Failed to sign in');
        }
      } else {
        setError(err.message || 'Failed to sign in');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfaCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await confirmMfaSignIn(mfaCode);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await signUp(username, password, email);
      setMode('confirm');
    } catch (err: any) {
      setError(err.message || 'Failed to sign up, try again');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await confirmSignUp(username, confirmCode);
      await signIn(username, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to confirm account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await resetPassword(username);
      setMode('resetCode');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await confirmResetPassword(username, confirmCode, newPassword);
      setSuccessMsg('Password reset! Signing you in...');
      await signIn(username, newPassword);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={styles.login}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <SignalLoader message="" size="small" tickers={loginTickers} />
          <span className={styles.brandName}>SignalDeck</span>
        </div>

        <h1 className={styles.title}>
          {mode === 'signIn' && 'Sign In'}
          {mode === 'signUp' && 'Create Account'}
          {mode === 'confirm' && 'Confirm Account'}
          {mode === 'forgotPassword' && 'Reset Password'}
          {mode === 'resetCode' && 'Enter Reset Code'}
          {mode === 'mfaCode' && 'Two-Factor Authentication'}
        </h1>

        {mode === 'signUp' && <p className={styles.subtitle}>Start trading smarter with automated signals.</p>}

        {error && <p className={styles.error}>{error}</p>}
        {successMsg && <p className={styles.success}>{successMsg}</p>}

        {mode === 'signIn' && (
          <form onSubmit={handleSignIn} className={styles.form}>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={styles.input}
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              required
            />
            <button type="submit" className={styles.button} disabled={submitting}>
              {submitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        {mode === 'signUp' && (
          <form onSubmit={handleSignUp} className={styles.form}>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={styles.input}
              required
            />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              required
            />
            <button type="submit" className={styles.button} disabled={submitting}>
              {submitting ? 'Creating account...' : 'Sign Up'}
            </button>
          </form>
        )}

        {mode === 'confirm' && (
          <form onSubmit={handleConfirm} className={styles.form}>
            <p className={styles.hint}>Check your email for a confirmation code.</p>
            <input
              type="text"
              placeholder="Confirmation code"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              className={styles.input}
              required
            />
            <button type="submit" className={styles.button} disabled={submitting}>
              {submitting ? 'Confirming...' : 'Confirm'}
            </button>
            <button
              type="button"
              className={styles.resendButton}
              onClick={async () => {
                setError('');
                setSuccessMsg('');
                try {
                  await resendSignUpCode(username);
                  setSuccessMsg('A new code has been sent to your email.');
                } catch (err: any) {
                  setError(err.message || 'Failed to resend code');
                }
              }}
            >
              Didn't get a code? Resend
            </button>
          </form>
        )}

        {mode === 'forgotPassword' && (
          <form onSubmit={handleForgotPassword} className={styles.form}>
            <p className={styles.hint}>Enter your username and we'll send a reset code to your email.</p>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={styles.input}
              required
            />
            <button type="submit" className={styles.button} disabled={submitting}>
              {submitting ? 'Sending code...' : 'Send Reset Code'}
            </button>
          </form>
        )}

        {mode === 'resetCode' && (
          <form onSubmit={handleResetPassword} className={styles.form}>
            <p className={styles.hint}>Check your email for a reset code.</p>
            <input
              type="text"
              placeholder="Reset code"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              className={styles.input}
              required
            />
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={styles.input}
              required
            />
            <button type="submit" className={styles.button} disabled={submitting}>
              {submitting ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}

        {mode === 'mfaCode' && (
          <form onSubmit={handleMfaCode} className={styles.form}>
            <p className={styles.hint}>Enter the 6-digit code from your authenticator app.</p>
            <input
              type="text"
              placeholder="000000"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className={styles.input}
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              autoFocus
              required
            />
            <button type="submit" className={styles.button} disabled={submitting || mfaCode.length !== 6}>
              {submitting ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        )}

        <div className={styles.toggle}>
          {mode === 'signIn' && (
            <>
              <p>
                Don't have an account?{' '}
                <button
                  className={styles.link}
                  onClick={() => {
                    setMode('signUp');
                    setError('');
                    setSuccessMsg('');
                  }}
                >
                  Sign up
                </button>
              </p>
              <p>
                <button
                  className={styles.link}
                  onClick={() => {
                    setMode('forgotPassword');
                    setError('');
                    setSuccessMsg('');
                  }}
                >
                  Forgot password?
                </button>
              </p>
            </>
          )}
          {mode === 'signUp' && (
            <p>
              Already have an account?{' '}
              <button
                className={styles.link}
                onClick={() => {
                  setMode('signIn');
                  setError('');
                  setSuccessMsg('');
                }}
              >
                Sign in
              </button>
            </p>
          )}
          {(mode === 'confirm' || mode === 'forgotPassword' || mode === 'resetCode' || mode === 'mfaCode') && (
            <p>
              <button
                className={styles.link}
                onClick={() => {
                  setMode('signIn');
                  setError('');
                  setSuccessMsg('');
                }}
              >
                Back to sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default Login;
